import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { collection, addDoc, serverTimestamp, doc, onSnapshot, setDoc, arrayUnion } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { db, auth } from '../config/firebase'; 
import useAdminStore from '../store/adminStore';
import { Lock, Mail, ChevronRight, ShieldCheck, User as UserIcon, ChevronLeft, ExternalLink, X, ChevronDown } from 'lucide-react';
import { PE_LOGO_SRC, PE_ONE_LOGO_SRC } from '../constants/brandAssets';

const ThemeToggle: React.FC = () => {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const toggle = () => {
    const next = !dark;
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('pe-theme', next ? 'dark' : 'light');
    setDark(next);
  };
  return (
    <button type="button" onClick={toggle} title={dark ? 'Light Mode' : 'Dark Mode'}
      className="flex items-center p-2 transition-all bg-pe-sunken hover:bg-pe-card rounded border border-pe-border dark:border-gray-800 hover:border-pe-accent/50 text-pe-muted hover:text-pe-accent-text h-[42px] shrink-0"
    >
      {dark ? (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
      )}
    </button>
  );
};

// --- KONFIGURATION & BASIS DATEN ---
const APP_ID = "1:432126252714:web:6f2c2d11827500648662d3";
const SIMPLIE_GUEST_UID_KEY = 'pe_simplie_guest_uid';

/** Lokale Gast-ID statt Firebase Anonymous Auth — erzeugt keinen Auth-Account. */
const getOrCreateGuestUid = (): string => {
    try {
        const existing = localStorage.getItem(SIMPLIE_GUEST_UID_KEY);
        if (existing && existing.trim()) return existing.trim();
        const id =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? `guest_${crypto.randomUUID()}`
                : `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem(SIMPLIE_GUEST_UID_KEY, id);
        return id;
    } catch {
        return `guest_${Date.now()}`;
    }
};
const levelDiscounts = { 0: 0.00, 1: 0.10, 2: 0.15, 3: 0.20 };
const WARENWIRTSCHAFT_FAKTOR = 1.05;
const REFERENCE_MWST_DE = 0.19;

const COUNTRY_CONFIG: Record<string, { name: string, mwst: number, currency: string }> = {
    DE: { name: 'Deutschland', mwst: 0.19, currency: 'EUR' },
    AT: { name: 'Österreich', mwst: 0.20, currency: 'EUR' },
    IT: { name: 'Italien', mwst: 0.22, currency: 'EUR' },
    CH: { name: 'Schweiz', mwst: 0.081, currency: 'CHF' }
};

const DEFAULT_SIMPLIE_EXTENSIONS = [
    { id: 'st_35', name: 'Simplie Tresse 35cm', packContent: '1 St.', baseEkNetto: 80.00 },
    { id: 'st_40', name: 'Simplie Tresse 40cm', packContent: '1 St.', baseEkNetto: 95.00 },
    { id: 'st_50', name: 'Simplie Tresse 50cm', packContent: '1 St.', baseEkNetto: 115.00 },
    { id: 'st_60', name: 'Simplie Tresse 60cm', packContent: '1 St.', baseEkNetto: 130.00 },
];

const REQUIRED_SIMPLIE_EXTENSION_IDS = DEFAULT_SIMPLIE_EXTENSIONS.map((ext) => ext.id);

const resolveExtensionsFromSnapshot = (cloudData: unknown): typeof DEFAULT_SIMPLIE_EXTENSIONS => {
    if (!Array.isArray(cloudData) || cloudData.length === 0) {
        return DEFAULT_SIMPLIE_EXTENSIONS.map((ext) => ({ ...ext }));
    }
    const hasAllRequired = REQUIRED_SIMPLIE_EXTENSION_IDS.every((id) =>
        cloudData.some((ext: { id?: string }) => ext?.id === id)
    );
    if (!hasAllRequired) {
        return DEFAULT_SIMPLIE_EXTENSIONS.map((ext) => ({ ...ext }));
    }
    return cloudData as typeof DEFAULT_SIMPLIE_EXTENSIONS;
};

const resolveIsP1AdminUser = (
    email: string | null | undefined,
    adminTeam: { email?: string; role?: string }[]
): boolean => {
    if (!email) return false;
    const userEmail = email.toLowerCase();
    if (userEmail === 'erfolg@friseur-coaching.de') return true;
    const member = adminTeam.find((m) => m.email && m.email.toLowerCase() === userEmail);
    if (!member) return false;
    return member.role === 'admin' || member.role === 'superadmin';
};

// --- HILFSFUNKTIONEN ---
const formatNumberDE = (val: any, opts = {}) => new Intl.NumberFormat('de-DE', opts).format(val || 0);
const formatCurrency = (val: any, curr: string, digits = 2) => `${formatNumberDE(val, { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${curr}`;
const formatCurrencyRounded = (val: any, curr: string) => formatCurrency(val, curr, 0);
const formatCurrencyStrict = (val: number, curr: string, digits = 2) => `${formatNumberDE(val, { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${curr}`;

const parseNumberDE = (str: any) => {
    if (typeof str === 'number') return str;
    if (!str) return 0;
    return parseFloat(String(str).replace(/\./g, '').replace(',', '.'));
};

// --- CSS INJEKTION (NOBLE SLATE / CARBON) ---
const CustomStyles = () => (
    <style>{`
      :root { 
          --custom-beige: #E6C299;
          --slate-800: #1e293b;
          --slate-900: #0f172a;
          --slate-700: #334155;
      }
      .text-customBeige { color: var(--custom-beige); text-shadow: 0 0 2px rgba(0,0,0,0.5); }
      .bg-customBeige { background-color: var(--custom-beige); }
      .border-customBeige { border-color: var(--custom-beige); }
      
      .font-numeric { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }

      .carbon-weave-bg {
          background-color: #080808;
          background-image:
              radial-gradient(circle at 100% 150%, #080808 24%, #111 24%, #111 28%, #080808 28%, #080808 36%, #111 36%, #111 40%, transparent 40%, transparent),
              radial-gradient(circle at 0 150%, #080808 24%, #111 24%, #111 28%, #080808 28%, #080808 36%, #111 36%, #111 40%, transparent 40%, transparent),
              radial-gradient(circle at 50% 100%, #111 10%, #080808 10%, #080808 23%, #111 23%, #111 30%, #080808 30%, #080808 43%, #111 43%, #111 50%, #080808 50%, #080808 63%, #111 63%, #111 71%, transparent 71%, transparent),
              radial-gradient(circle at 100% 50%, #111 5%, #080808 5%, #080808 15%, #111 15%, #111 20%, #080808 20%, #080808 29%, #111 29%, #111 34%, #080808 34%, #080808 44%, #111 44%, #111 49%, transparent 49%, transparent),
              radial-gradient(circle at 0 50%, #111 5%, #080808 5%, #080808 15%, #111 15%, #111 20%, #080808 20%, #080808 29%, #111 29%, #111 34%, #080808 34%, #080808 44%, #111 44%, #111 49%, transparent 49%, transparent);
          background-size: 20px 20px;
      }

      .carbon-panel {
          position: relative;
          border-radius: 1.5rem;
          border: 1px solid #1a1a1a;
          box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.02);
          overflow: hidden;
          z-index: 1;
      }

      .carbon-panel::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.65));
          pointer-events: none;
          z-index: 1;
      }

      .carbon-content { position: relative; z-index: 10; }

      html:not(.dark) .bg-deep-space { background-color: var(--pe-bg-primary); }
      html:not(.dark) .carbon-weave-bg { background-color: var(--pe-bg-elevated); background-image: none; }
      html:not(.dark) .carbon-panel { background-color: var(--pe-bg-card); border-color: var(--pe-border-subtle); box-shadow: 0 12px 28px -12px rgba(28,25,23,0.18); }
      html:not(.dark) .carbon-panel::after { background: none; }

      .carbon-panel.simplie-main-panel {
          overflow: visible;
      }

      .carbon-panel.simplie-main-panel .carbon-content {
          overflow: visible;
      }

      .simplie-tooltip-section {
          overflow: visible;
      }

      .simplie-article-panel {
          overflow: visible !important;
      }

      .simplie-article-table-scroll {
          overflow-x: auto;
          overflow-y: visible;
      }
    `}</style>
);

// --- NOBLE SELECT (Portal: nicht vom sticky Header / overflow-x abgeschnitten) ---
const NobleSelect = ({ value, options, onChange, label, className = "" }: any) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const menuWidth = Math.max(rect.width, 168);
        const estimatedHeight = Math.min((options?.length || 4) * 42 + 8, 280);
        const spaceBelow = window.innerHeight - rect.bottom - 8;
        const openUp = spaceBelow < estimatedHeight && rect.top > spaceBelow;
        const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
        setMenuStyle({
            position: 'fixed',
            left,
            width: menuWidth,
            zIndex: 2147483000,
            maxHeight: Math.min(280, openUp ? rect.top - 12 : spaceBelow),
            ...(openUp
                ? { bottom: window.innerHeight - rect.top + 4, top: 'auto' as any }
                : { top: rect.bottom + 4, bottom: 'auto' as any }),
        });
    }, [options?.length]);

    useEffect(() => {
        if (!isOpen) return;
        updatePosition();
        const onReposition = () => updatePosition();
        window.addEventListener('scroll', onReposition, true);
        window.addEventListener('resize', onReposition);
        return () => {
            window.removeEventListener('scroll', onReposition, true);
            window.removeEventListener('resize', onReposition);
        };
    }, [isOpen, updatePosition]);

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            const t = event.target;
            if (triggerRef.current?.contains(t)) return;
            if (menuRef.current?.contains(t)) return;
            setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find((o: any) => o.value === value) || options[0];

    return (
        <div className={className}>
            <button 
                type="button"
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)} 
                className="flex items-center justify-between w-full border border-[#E6C299] rounded bg-pe-sunken px-3 py-1.5 shadow-[0_0_8px_rgba(230,194,153,0.15)] outline-none focus:ring-1 focus:ring-[#E6C299] transition-colors hover:bg-pe-card h-[34px] sm:h-[42px]"
            >
                <div className="flex items-center">
                    {label && <span className="text-xs text-stone-500 dark:text-gray-500 mr-2 uppercase font-bold">{label}</span>}
                    <span className="text-pe-accent-text font-bold text-xs sm:text-sm">{selectedOption?.label}</span>
                </div>
                <ChevronDown size={14} className={`ml-2 text-pe-accent-text transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && createPortal(
                <div
                    ref={menuRef}
                    style={menuStyle}
                    className="bg-pe-tile-header border border-[#E6C299]/50 rounded-lg shadow-2xl overflow-y-auto"
                >
                    {options.map((opt: any) => (
                        <div 
                            key={opt.value} 
                            onClick={() => { onChange(opt.value); setIsOpen(false); }} 
                            className={`px-4 py-2.5 cursor-pointer text-xs sm:text-sm font-bold border-b border-pe-border dark:border-gray-800/50 last:border-0 transition-colors whitespace-nowrap
                                ${value === opt.value ? 'bg-[#E6C299]/20 text-pe-text' : 'text-pe-accent-text hover:bg-[#E6C299]/10 hover:text-pe-text'}
                            `}
                        >
                            {opt.label}
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

// --- UI KOMPONENTEN ---
const LocalTooltip = ({ children, text, className="flex items-center", position = "bottom" }: any) => {
    const isBottom = position === 'bottom';
    return (
        <div className={`relative group ${className}`}>
            {children}
            <div
                className={`absolute hidden group-hover:block z-[9999] w-64 p-3 text-sm font-bold text-black bg-[#E6C299] rounded shadow-xl left-1/2 -translate-x-1/2 pointer-events-none ${
                    isBottom ? 'top-full mt-3' : 'bottom-full mb-3'
                }`}
            >
                {isBottom ? (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 border-4 border-transparent border-b-[#E6C299]"></div>
                ) : (
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#E6C299]"></div>
                )}
                {text}
            </div>
        </div>
    );
};

/** Mindest-Verkaufsmarge für manuelle Wunsch-Marge (Simplie-System). */
const WUNSCHMARGE_MIN = 80;

const HeaderInput = ({ label, tooltip, children }: any) => (
    <div className="flex flex-col gap-1 w-full simplie-tooltip-section">
        <div className="flex justify-center">
            <label className="text-xs font-bold uppercase tracking-wider text-pe-muted dark:text-gray-400">{label}</label>
        </div>
        {tooltip ? (
            <LocalTooltip text={tooltip} className="w-full cursor-help">
                {children}
            </LocalTooltip>
        ) : (
            children
        )}
    </div>
);

const AuswertungResult = ({ label, value, unit, subtext, additionalInfo, quantityLabel, variant, tooltip }: any) => {
    const isHighlight = variant === 'highlight';
    const bgClass = isHighlight 
        ? "bg-pe-sunken border-[#E6C299]/40 shadow-[0_0_20px_rgba(230,194,153,0.1)]" 
        : "bg-pe-tile-header border-pe-border dark:border-[#222]";

    const valueClass = isHighlight ? 'text-4xl lg:text-5xl text-pe-accent-text' : 'text-3xl lg:text-3xl text-pe-text';
    const unitClass = isHighlight ? 'text-2xl lg:text-3xl text-stone-500 dark:text-gray-500 ml-2' : 'text-lg lg:text-xl text-stone-500 dark:text-gray-500 ml-1';
    const qty = String(quantityLabel || '').trim();

    const card = (
        <div className={`flex flex-col gap-2 text-center p-6 rounded-xl h-full justify-start shadow-inner transition-all duration-300 border ${bgClass} ${tooltip ? 'cursor-help' : ''}`}>
            <label className={`text-xs font-bold uppercase tracking-widest ${isHighlight ? 'text-[#d4b088]' : 'text-stone-500 dark:text-gray-500'}`}>{label}</label>
            <div className="flex flex-col items-center mt-2">
                <div className="flex items-baseline">
                    <span className={`font-bold tracking-tight font-mono whitespace-nowrap ${valueClass}`}>
                        {formatNumberDE(value, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </span>
                    {unit && <span className={`font-bold font-sans ${unitClass}`}>{unit}</span>}
                </div>
                {subtext && <span className="text-xs text-stone-500 dark:text-gray-500 mt-1 uppercase tracking-widest font-bold">{subtext}</span>}
                {qty ? (
                    <span className="mt-2 inline-flex items-center rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/10 px-2.5 py-1 text-sm font-black font-mono text-pe-accent-text tracking-wide">
                        {qty}
                    </span>
                ) : null}
            </div>
            {additionalInfo && <div className="text-[10px] text-gray-600 mt-3 pt-3 border-t border-[#1a1a1a] uppercase tracking-widest">{additionalInfo}</div>}
        </div>
    );

    if (!tooltip) return card;

    return (
        <LocalTooltip text={tooltip} className="w-full h-full block">
            {card}
        </LocalTooltip>
    );
};

const ToggleSwitch = ({ enabled, onChange }: any) => (
    <button type="button" onClick={onChange} className={`relative inline-flex h-6 w-11 rounded-full transition-colors focus:outline-none ${enabled ? 'bg-green-600' : 'bg-gray-700'}`}>
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform mt-0.5 ml-0.5 ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
);

const Footer = () => {
    const teamsLink = "https://teams.microsoft.com/l/meetup-join/19%3ameeting_MTdhOWZiMWQtNDIzMi00MDkyLTg2NmMtMTBhNzA1OWM3YjUy%40thread.v2/0?context=%7b%22Tid%22%3a%223f4a4c34-4ad2-4564-a4c8-2fb6130916c7%22%2c%22Oid%22%3a%22fb268932-47d2-4ad5-951c-0342edb15a11%22%7d";
    return (
        <footer className="bg-pe-sunken border-t border-pe-border dark:border-gray-800 text-pe-muted dark:text-gray-400 mt-auto relative z-10 w-full">
            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
                    <div className="lg:col-span-1">
                        <img
                            src={PE_LOGO_SRC}
                            alt="PreisExpert Friseurcoaching"
                            className="h-24 sm:h-28 w-auto object-contain mb-4"
                        />
                        <p className="text-xs text-stone-500 dark:text-gray-500">Rotehahnstraße 2 - 21335 Lüneburg</p>
                        <p className="text-[10px] text-gray-600 mt-2">© {new Date().getFullYear()} PreisExpert Friseurcoaching.</p>
                    </div>
                    <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-8">
                        <div>
                            <h3 className="text-[10px] font-black text-pe-text tracking-widest uppercase mb-2">TachelesReden</h3>
                            <div className="mt-3 max-w-[220px]">
                                <a href="https://www.instagram.com/friseurtacheles?utm_source=qr&igsh=NGRrN3F3b3k5dndn" target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden border border-[#E6C299]/25 shadow-lg hover:border-[#E6C299]/50 transition-all bg-pe-tile-header">
                                    <img 
                                        className="w-full h-auto object-contain block opacity-95 hover:opacity-100 transition-opacity" 
                                        src="/assets/tacheles-reden.jpeg?v=20260727" 
                                        alt="TachelesReden – jeden ersten Mittwoch im Monat" 
                                    />
                                </a>
                            </div>
                        </div>
                        <div>
                            <p className="mt-8 text-xs text-pe-muted dark:text-gray-400 leading-relaxed">jeden ersten Mittwoch im Monat um 19:30 Uhr mit spannenden Themen & Gästen.</p>
                             <p className="mt-4 text-xs text-pe-muted dark:text-gray-400 leading-relaxed">Sei Live dabei auf <a href={teamsLink} target="_blank" rel="noreferrer" className="text-pe-accent-text hover:underline">Teams</a>, <a href="https://www.instagram.com/friseurtacheles?utm_source=qr&igsh=NGRrN3F3b3k5dndn" target="_blank" rel="noreferrer" className="hover:text-pe-text underline">Instagram</a> oder <a href="https://www.facebook.com/PreisExpert" target="_blank" rel="noreferrer" className="hover:text-pe-text underline">Facebook</a>.</p>
                        </div>
                    </div>
                    <div className="lg:col-span-2 flex flex-col justify-start space-y-4">
                        <h3 className="text-[10px] font-black text-pe-text tracking-widest uppercase mb-2">Support & Impressum</h3>
                        <div className="flex space-x-6">
                            <a href="https://www.friseur-coaching.de/impressum/" target="_blank" rel="noreferrer" className="text-xs hover:text-pe-accent-text transition-colors">Impressum</a>
                            <a href="https://www.friseur-coaching.de/datenschutzerklaerung/" target="_blank" rel="noreferrer" className="text-xs hover:text-pe-accent-text transition-colors">Datenschutz</a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

const StepperInput = ({ value, onValueChange, min = 0, step = 1, unit, allowEmpty = false, isGold = false }: any) => {
    const handleChange = (e: any) => {
        const val = e.target.value;
        if (val === '' && allowEmpty) { onValueChange(''); return; }
        const parsed = parseInt(val.replace(/\D/g, ''), 10);
        if (!isNaN(parsed)) onValueChange(parsed);
    };

    const handleBlur = () => {
        if (value === '' && !allowEmpty) onValueChange(min);
        else if (value !== '' && value < min) onValueChange(min);
    };

    const dec = () => {
        if (value === '' && allowEmpty) return;
        onValueChange(Math.max(min, Number(value) - step));
    };

    const inc = () => {
        // Leer → zuerst auf min springen (nicht min+step), damit Wunschmarge bei 80 startet
        if (value === '') onValueChange(min);
        else onValueChange(Number(value) + step);
    };

    const containerClass = isGold
        ? "flex items-stretch w-full bg-[#FBF3E7] dark:bg-[#1a1400] border border-[#8D6435] dark:border-[#E6C299] shadow-[0_0_10px_rgba(230,194,153,0.15)] rounded h-[42px] overflow-hidden transition-colors"
        : "flex items-stretch w-full bg-pe-sunken border border-pe-border dark:border-gray-800 rounded h-[42px] overflow-hidden focus-within:border-[#E6C299] transition-colors";
    
    const btnClass = isGold
        ? "px-4 bg-[#E6C299]/10 hover:bg-[#E6C299]/20 text-pe-accent-text font-bold border-pe-border dark:border-gray-800 transition-colors"
        : "px-4 bg-pe-card hover:bg-pe-hover dark:hover:bg-gray-800 text-pe-accent-text font-bold border-pe-border dark:border-gray-800 transition-colors";

    return (
        <div className={containerClass}>
            <button type="button" onClick={dec} className={`${btnClass} border-r ${isGold ? 'border-[#E6C299]/30' : 'border-pe-border dark:border-gray-800'}`}>-</button>
            <div className="flex-1 flex items-center justify-center bg-transparent px-1 relative min-w-0">
                <input
                    type="text"
                    value={value}
                    onChange={handleChange}
                    onFocus={(e) => e.currentTarget.select()}
                    onClick={(e) => e.currentTarget.select()}
                    onBlur={handleBlur}
                    className={`w-full bg-transparent ${isGold ? 'text-pe-accent-text' : 'text-pe-text'} font-bold font-mono text-center outline-none placeholder-stone-400 dark:placeholder-gray-600`}
                    placeholder={allowEmpty ? "Auto" : ""}
                />
                {unit && value !== '' ? (
                    <span
                        className={`${isGold ? 'text-[#8D6435]/60 dark:text-[#E6C299]/60' : 'text-stone-500 dark:text-gray-500'} text-xs absolute right-2 font-sans pointer-events-none`}
                    >
                        {unit}
                    </span>
                ) : null}
            </div>
            <button type="button" onClick={inc} className={`${btnClass} border-l ${isGold ? 'border-[#E6C299]/30' : 'border-pe-border dark:border-gray-800'}`}>+</button>
        </div>
    );
};

const EditableField = ({ value, onValueChange, isNumeric, unit, formatOptions = {}, disabled }: any) => {
    const format = useCallback((v: any) => (v != null && isNumeric) ? formatNumberDE(v, formatOptions) : v || '', [isNumeric, formatOptions]);
    const [disp, setDisp] = useState(format(value));
    
    useEffect(() => setDisp(format(value)), [value, format]);
    
    const handleBlur = () => {
        if (!isNumeric) { if (disp !== value) onValueChange(disp); return; }
        const num = parseNumberDE(disp);
        if (isNaN(num)) setDisp(format(value)); else onValueChange(num);
    };
    
    return (
        <div className={`flex items-center justify-center rounded w-full p-2 border border-pe-border dark:border-gray-800 ${disabled ? 'bg-stone-200 dark:bg-gray-800 opacity-50' : 'bg-pe-sunken'} ${disabled ? 'cursor-not-allowed' : 'cursor-text'} focus-within:border-[#E6C299] transition-colors shadow-inner`}>
            <div className="flex items-baseline justify-center w-full">
                <input type="text" value={disp} onChange={e => setDisp(e.target.value)} onFocus={e => e.target.select()} onBlur={handleBlur} onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()} disabled={disabled} className={`text-right text-pe-text font-semibold font-mono bg-transparent outline-none p-0 m-0 w-full ${disabled ? 'text-stone-500 dark:text-gray-500' : ''}`} />
                {unit && <span className="text-stone-500 dark:text-gray-500 font-medium ml-1 text-sm font-sans">{unit}</span>}
            </div>
        </div>
    );
};

const ReadOnlyField = ({ value, unit, isNumeric, formatOptions, dimmed }: any) => (
    <div className={`flex items-center justify-center bg-pe-card border border-pe-border dark:border-gray-800 shadow-inner rounded w-full p-2 text-center transition-opacity ${dimmed ? 'opacity-30' : 'opacity-100'}`}>
        <span className="text-pe-accent-text font-semibold font-mono">{isNumeric ? formatNumberDE(value, formatOptions) : value}</span>
        {unit && <span className="text-stone-500 dark:text-gray-500 font-semibold ml-1 text-sm font-sans">{unit}</span>}
    </div>
);

const Modal = ({ isOpen, onClose, title, children }: any) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-stone-900/50 dark:bg-[#0a0a0a]/90 flex items-center justify-center z-[100] backdrop-blur-sm p-4">
            <div className="bg-[#0f0f10] border border-[#E6C299]/30 rounded-2xl shadow-[0_0_50px_rgba(230,194,153,0.1)] max-w-lg w-full overflow-hidden">
                <div className="flex justify-between items-center p-5 border-b border-pe-border dark:border-gray-800 bg-pe-card">
                    <h2 className="text-lg font-black text-pe-accent-text uppercase tracking-widest">{title}</h2>
                    <button onClick={onClose} className="text-stone-500 dark:text-gray-500 hover:text-pe-text transition-colors"><X size={24} /></button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
};

const SimpleMarginCalculator = ({ currency, mwst }: { currency: string, mwst: number }) => {
    const [mr, setMr] = useState({ ek: 80, marge: 100 });
    const [vr, setVr] = useState({ ek: 68, vk: 160 });
    
    const de_brutto_ref = mr.ek * (1 + mr.marge / 100);
    const basis_netto = de_brutto_ref / (1 + REFERENCE_MWST_DE);
    const real_vk_brutto = basis_netto * (1 + mwst);
    
    const vr_vk_netto = vr.vk / (1 + mwst);
    const vr_marge = vr.ek > 0 ? ((vr_vk_netto / vr.ek) - 1) * 100 : 0;
    
    const H = ({c}: any) => <div className="text-stone-500 dark:text-gray-500 text-[10px] font-bold uppercase tracking-widest text-center pb-1">{c}</div>;
    
    return (
        <div className="space-y-8">
            <div className="bg-pe-sunken p-5 rounded-xl border border-pe-border dark:border-gray-800 shadow-inner space-y-4">
                <div className="grid grid-cols-3 gap-3"><H c="EK Preis"/><H c="Marge %"/><H c="VK Preis (Brutto)"/></div>
                <div className="grid grid-cols-3 gap-3 items-center">
                    <EditableField value={mr.ek} onValueChange={(v: any) => setMr(s => ({...s, ek: v}))} isNumeric unit={currency} formatOptions={{minimumFractionDigits: 2}} />
                    <EditableField value={mr.marge} onValueChange={(v: any) => setMr(s => ({...s, marge: v}))} isNumeric unit="%" />
                    <div className="bg-pe-card border border-[#E6C299]/30 rounded-lg p-2 text-center text-pe-accent-text font-bold flex items-center justify-center font-mono h-[42px] shadow-[0_0_10px_rgba(230,194,153,0.1)]">
                        {formatNumberDE(real_vk_brutto, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        <span className="text-stone-500 dark:text-gray-500 ml-1 text-xs font-sans uppercase">{currency}</span>
                    </div>
                </div>
            </div>
            <div className="bg-pe-sunken p-5 rounded-xl border border-pe-border dark:border-gray-800 shadow-inner space-y-4">
                <div className="grid grid-cols-3 gap-3"><H c="EK Preis"/><H c="VK Preis (Brutto)"/><H c="VK-Marge % ="/></div>
                <div className="grid grid-cols-3 gap-3 items-center">
                    <EditableField value={vr.ek} onValueChange={(v: any) => setVr(s => ({...s, ek: v}))} isNumeric unit={currency} formatOptions={{minimumFractionDigits: 2}} />
                    <EditableField value={vr.vk} onValueChange={(v: any) => setVr(s => ({...s, vk: v}))} isNumeric unit={currency} formatOptions={{minimumFractionDigits: 2}} />
                    <div className="bg-pe-card border border-[#E6C299]/30 rounded-lg p-2 text-center text-pe-accent-text font-bold flex items-center justify-center font-mono h-[42px] shadow-[0_0_10px_rgba(230,194,153,0.1)]">
                        {formatNumberDE(vr_marge, {maximumFractionDigits: 0})}
                        <span className="text-stone-500 dark:text-gray-500 ml-1 text-xs font-sans">%</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENTS ---

const Gatekeeper = ({ onAuthenticated, onTeamAdminAccess }: { onAuthenticated: () => void; onTeamAdminAccess: () => void }) => {
    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailPrompted, setEmailPrompted] = useState(false); 

    const [dbCreds, setDbCreds] = useState({ username: 'Simplie-Pro', password: 'Simp_25' });

    useEffect(() => {
        const credsRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'loginCredentials');
        const unsubscribe = onSnapshot(credsRef, (docSnap) => {
            if (docSnap.exists()) setDbCreds(docSnap.data() as any);
        });
        return () => unsubscribe();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (username.trim() === 'Admin' && password === '90') {
            onTeamAdminAccess();
            return;
        }

        if (username.trim() !== dbCreds.username || password !== dbCreds.password) {
            setError('Zugriff verweigert. Benutzername oder Passwort inkorrekt.');
            return;
        }

        if (!email.trim()) {
            if (!emailPrompted) {
                setError('Bitte E-Mail-Adresse eingeben (oder erneut klicken, um ohne E-Mail fortzufahren).');
                setEmailPrompted(true);
                return; 
            }
        } else if (!email.includes('@')) {
            setError('Bitte eine gültige E-Mail-Adresse eingeben.');
            return;
        }

        setIsSubmitting(true);
        try {
            if (email.trim()) {
                const cleanEmail = email.trim().toLowerCase();
                
                await addDoc(collection(db, 'leads/extensions'), {
                    email: cleanEmail,
                    username: username.trim(),
                    source: 'Simplie Public Calculator',
                    capturedAt: serverTimestamp()
                });

                const globalContactsRef = doc(db, 'newsletter_system', 'global_contacts');
                await setDoc(globalContactsRef, {
                    externalLeads: arrayUnion(cleanEmail),
                    updatedAt: new Date().toISOString()
                }, { merge: true });
            }
        } catch (err) {
            console.error("Silent Capture Firebase Error:", err);
        } finally {
            setIsSubmitting(false);
            onAuthenticated();
        }
    };

    return (
        <div className="carbon-panel carbon-weave-bg shadow-2xl p-6 sm:p-10 max-w-lg w-full mx-auto relative overflow-hidden border border-[#E6C299]/20">
            <div className="carbon-content">
                    <div className="text-center mb-6">
                        <img src="https://www.friseur-coaching.de/wp-content/uploads/2025/09/simplie-Logofinal_croppedl.png" alt="Simplie Logo" className="h-16 sm:h-20 w-auto mx-auto mb-5 bg-white p-2.5 rounded-xl shadow-lg" />
                        <h2 className="text-3xl font-black text-pe-text uppercase tracking-widest drop-shadow-md">Willkommen</h2>
                        <div className="flex flex-col items-center mt-2 gap-4">
                            <span className="text-pe-accent-text text-xs uppercase tracking-widest font-bold">Exklusiver Zugang für Simplie-Partner</span>
                            <div className="flex flex-col items-center gap-3 border-t border-pe-border dark:border-gray-800/80 pt-5 mt-2 w-full">
                                <span className="text-[10px] text-stone-500 dark:text-gray-500 uppercase tracking-widest font-bold">Powered by</span>
                                <img
                                    src={PE_LOGO_SRC}
                                    alt="PreisExpert"
                                    className="h-28 sm:h-32 w-auto max-w-[90%] object-contain drop-shadow-lg"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#0f1423] p-6 rounded-xl text-center border border-pe-border dark:border-gray-800 shadow-inner mb-8">
                        <p className="text-pe-muted dark:text-gray-300 text-sm mb-4 font-medium leading-relaxed">Liebe Simplie-Pro´s, die Zugangsdaten findet Ihr im Pro-Account!</p>
                        <a 
                            href="https://pro.simpliehair.com/user-login/" 
                            target="_blank" 
                            rel="noreferrer" 
                            className="text-pe-accent-text hover:text-pe-text font-black uppercase tracking-widest text-xs underline underline-offset-4 decoration-[#E6C299]/50 hover:decoration-white transition-colors inline-flex items-center gap-1.5"
                        >
                            Zum Pro-Account <ExternalLink size={14} />
                        </a>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-5">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-pe-muted dark:text-gray-400 mb-2">Ihre E-Mail-Adresse</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Mail size={18} className="text-stone-500 dark:text-gray-500" />
                                    </div>
                                    <input 
                                        type="email" 
                                        value={email} 
                                        onChange={e => { setEmail(e.target.value); if (emailPrompted) setEmailPrompted(false); }} 
                                        className={`w-full bg-pe-input dark:bg-[#000] text-pe-text text-sm pl-12 p-4 rounded-xl border ${emailPrompted && !email ? 'border-red-500/50 focus:border-red-500' : 'border-pe-border dark:border-gray-800 focus:border-[#E6C299]'} focus:ring-1 focus:ring-[#E6C299] outline-none transition-all shadow-inner`} 
                                        placeholder="name@salon.de"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-pe-muted dark:text-gray-400 mb-2">Benutzername</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <UserIcon size={18} className="text-stone-500 dark:text-gray-500" />
                                    </div>
                                    <input 
                                        type="text" 
                                        value={username} 
                                        onChange={e => setUsername(e.target.value)} 
                                        className="w-full bg-pe-input dark:bg-[#000] text-pe-text text-sm pl-12 p-4 rounded-xl border border-pe-border dark:border-gray-800 focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299] outline-none transition-all shadow-inner" 
                                        placeholder="z.B. Simplie-Pro"
                                        required 
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-pe-muted dark:text-gray-400 mb-2">Simplie-Passwort</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Lock size={18} className="text-stone-500 dark:text-gray-500" />
                                    </div>
                                    <input 
                                        type="password" 
                                        value={password} 
                                        onChange={e => setPassword(e.target.value)} 
                                        className="w-full bg-pe-input dark:bg-[#000] text-pe-text text-sm pl-12 p-4 rounded-xl border border-pe-border dark:border-gray-800 focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299] outline-none transition-all shadow-inner font-mono tracking-widest" 
                                        placeholder="••••••••"
                                        required 
                                    />
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="bg-red-900/20 border border-red-500/30 text-red-400 text-sm text-center font-bold p-4 rounded-xl animate-in slide-in-from-top-2">
                                {error}
                            </div>
                        )}

                        <button 
                            type="submit" 
                            disabled={isSubmitting}
                            className={`w-full text-black font-black py-4 rounded-xl uppercase tracking-widest text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-2
                                ${emailPrompted && !email ? 'bg-red-500 hover:bg-red-400 shadow-[0_0_20px_rgba(239,68,68,0.2)]' : 'bg-[#E6C299] hover:bg-[#d4b088] shadow-[0_0_20px_rgba(230,194,153,0.15)] hover:shadow-[0_0_30px_rgba(230,194,153,0.3)]'}
                            `}
                        >
                            {isSubmitting ? 'Wird autorisiert...' : (emailPrompted && !email ? 'Ohne E-Mail starten' : 'Kalkulator starten')}
                            {!isSubmitting && <ChevronRight size={20} />}
                        </button>

                        <div className="mt-8 pt-6 border-t border-pe-border dark:border-gray-800/50 flex flex-col items-center text-center gap-3">
                            <ShieldCheck size={24} className="text-[#8D6435]/70 dark:text-[#E6C299]/70" />
                            <p className="text-[10px] leading-relaxed text-stone-500 dark:text-gray-500 font-medium">
                                Deine Daten sind bei uns sicher und werden <strong className="text-pe-muted dark:text-gray-400">nicht an Dritte weitergegeben</strong>. 
                                Wir nutzen deine E-Mail ausschließlich für Updates zum Kalkulator und relevante PreisExpert-Informationen. Kein Spam.
                            </p>
                        </div>
                    </form>
            </div>
        </div>
    );
};

const CalculatorShell = ({ isAdmin, userId }: { isAdmin: boolean, userId: string }) => {
    const [inputs, setInputs] = useState({ menge: 1, stundensatz: 120, einarbeitungszeit: 60, herrenhaarschnittPreis: 37.50 });
    const [baseExtensions, setBaseExtensions] = useState<any[] | null>(null);
    const [selectedLevel, setSelectedLevel] = useState(0);
    const [selectedExtensionId, setSelectedExtensionId] = useState<string | null>(null);
    const [lineItems, setLineItems] = useState<Array<{ extensionId: string; menge: number }>>([]);
    
    const [isAdminVisible, setIsAdminVisible] = useState(false);
    const [isAdminUnlockOpen, setIsAdminUnlockOpen] = useState(false);
    const [adminUnlockPassword, setAdminUnlockPassword] = useState('');
    const [adminUnlockError, setAdminUnlockError] = useState('');
    const [isAdminPanelUnlocked, setIsAdminPanelUnlocked] = useState(false);
    const [isMarginCalcOpen, setIsMarginCalcOpen] = useState(false);
    const adminSectionRef = useRef<HTMLDivElement>(null);
    const [customMargin, setCustomMargin] = useState<number | null>(null);
    const [isNotificationsEnabled, setIsNotificationsEnabled] = useState<boolean | null>(null);
    const [loginCredentials, setLoginCredentials] = useState({ username: 'Simplie-Pro', password: 'Simp_25' });
    
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [temporaereMarge, setTemporaereMarge] = useState<string | number>(''); 
    
    const [selectedCountry, setSelectedCountry] = useState('DE');
    const currentMwst = COUNTRY_CONFIG[selectedCountry].mwst;
    const currentCurrency = COUNTRY_CONFIG[selectedCountry].currency;
    const didInitSelectionRef = useRef(false);

    // DATEN LADEN (Aus Firestore)
    useEffect(() => {
        if (!userId) return;
        
        const marginRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'customMargin');
        const extRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'extensions');
        const notifRef = doc(db, 'artifacts', APP_ID, 'users', userId, 'settings', 'notifications');
        const credsRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'loginCredentials');

        const unsubMargin = onSnapshot(marginRef, s => setCustomMargin(s.exists() ? s.data().value : 78.5));
        const unsubExt = onSnapshot(extRef, s => {
            const cloudData = s.exists() ? s.data().data : null;
            const d = resolveExtensionsFromSnapshot(cloudData);
            setBaseExtensions(d);
            // Einmalige Initialauswahl — nicht erneut erzwingen nach manuellem Abwählen
            if (!didInitSelectionRef.current && d.length > 0) {
                didInitSelectionRef.current = true;
                const firstId = d[0].id;
                setSelectedExtensionId(prev => prev || firstId);
                setLineItems(prev => prev.length > 0 ? prev : [{ extensionId: firstId, menge: 1 }]);
            }
        });
        const unsubNotif = onSnapshot(notifRef, s => setIsNotificationsEnabled(s.exists() ? s.data().value : true));
        const unsubCreds = onSnapshot(credsRef, s => { if(s.exists()) setLoginCredentials(s.data() as any); });

        return () => { unsubMargin(); unsubExt(); unsubNotif(); unsubCreds(); };
    }, [userId]);

    const gesamtMenge = lineItems.reduce((sum, li) => sum + (Number(li.menge) || 0), 0);

    // Globale Mengenanzeige = Summe der lineItems
    useEffect(() => {
        setInputs(prev => (prev.menge === gesamtMenge ? prev : { ...prev, menge: gesamtMenge }));
    }, [gesamtMenge]);

    const toggleLineItem = (extensionId: string, checked: boolean) => {
        if (checked) {
            setLineItems(prev => {
                if (prev.some(li => li.extensionId === extensionId)) return prev;
                const next = [...prev, { extensionId, menge: 1 }];
                setSelectedExtensionId(next[0]?.extensionId ?? null);
                return next;
            });
        } else {
            setLineItems(prev => {
                const next = prev.filter(li => li.extensionId !== extensionId);
                setSelectedExtensionId(next[0]?.extensionId ?? null);
                return next;
            });
        }
    };

    const updateLineItemMenge = (extensionId: string, mengeRaw: number) => {
        const menge = !isNaN(mengeRaw) && mengeRaw > 0 ? mengeRaw : 1;
        setLineItems(prev => prev.map(li => li.extensionId === extensionId ? { ...li, menge } : li));
    };

    // 💉 CHIRURGIE-FIX: Funktionen werden wieder genutzt in der reparierten Admin-Tabelle
    const updateExtensionsInFirestore = async (newExtensions: any[]) => {
        if (!isAdmin) return;
        try {
            await setDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'extensions'), { data: newExtensions }, { merge: true });
            setSuccessMessage('Artikel gespeichert!');
            setTimeout(() => setSuccessMessage(null), 2000);
        } catch (e) { console.error(e); }
    };

    const handleBaseExtensionChange = (id: string, field: string, value: any) => {
        setBaseExtensions(prev => {
            if(!prev) return prev;
            const newExtensions = prev.map(ext => ext.id === id ? { ...ext, [field]: value } : ext);
            updateExtensionsInFirestore(newExtensions);
            return newExtensions;
        });
    };

    const addExtension = () => {  
        if (!isAdmin) return;
        const newId = `custom_${Date.now()}`;
        const newExtension = { id: newId, name: 'Neuer Artikel', packContent: '1 St.', baseEkNetto: 0 };
        setBaseExtensions(prev => {
            const newExtensions = [...(prev || []), newExtension];
            updateExtensionsInFirestore(newExtensions);
            return newExtensions;
        });
        setLineItems(prev => {
            const next = [...prev, { extensionId: newId, menge: 1 }];
            setSelectedExtensionId(newId);
            return next;
        });
    };

    const deleteExtension = (id: string) => {
        if (!isAdmin) return;
        if (!window.confirm("Möchtest du diesen Artikel wirklich aus der Datenbank löschen?")) return;
        setBaseExtensions(prev => {
            if(!prev) return prev;
            const newExtensions = prev.filter(ext => ext.id !== id);
            updateExtensionsInFirestore(newExtensions);
            return newExtensions;
        });
        setLineItems(prev => {
            const next = prev.filter(li => li.extensionId !== id);
            setSelectedExtensionId(next[0]?.extensionId ?? null);
            return next;
        });
    };

    const handleCustomMarginChange = async (value: number) => {
        if (!isAdmin || !userId) return;
        setCustomMargin(value);
        setSuccessMessage(null);
        try {
            await setDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'customMargin'), { value: value }, { merge: true });
            setSuccessMessage('Marge gespeichert!');
        } catch (e) {
            setSuccessMessage('Fehler beim Speichern.');
        } finally {
            setTimeout(() => setSuccessMessage(null), 3000);
        }
    };

    const handleToggleNotifications = async () => {
        if (!userId || !isAdmin) return;
        try {
            const newValue = !isNotificationsEnabled;
            await setDoc(doc(db, 'artifacts', APP_ID, 'users', userId, 'settings', 'notifications'), { value: newValue }, { merge: true });
            setSuccessMessage('Gespeichert!');
        } catch (e) {
            setSuccessMessage('Fehler beim Speichern.');
        } finally {
            setTimeout(() => setSuccessMessage(null), 3000);
        }
    };

    const updateCredentialsInFirestore = async (newCredentials: any) => {
        if (!isAdmin || !userId) return;
        try {
            await setDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'loginCredentials'), newCredentials, { merge: true });
            setSuccessMessage('Zugangsdaten gespeichert!');
        } catch (e) {
            setSuccessMessage('Fehler beim Speichern.');
        } finally {
            setTimeout(() => setSuccessMessage(null), 3000);
        }
    };

    const extensions = useMemo(() => baseExtensions ? baseExtensions.map(ext => {
        const discount = (levelDiscounts as any)[selectedLevel] || 0;
        const ekNetto = ext.baseEkNetto * (1 - discount);
        
        const vkBruttoReferenzDE = ext.baseEkNetto * (1 + (customMargin || 100) / 100);
        const vkNettoBasis = vkBruttoReferenzDE / (1 + REFERENCE_MWST_DE);
        const vkBruttoFixStandard = vkNettoBasis * (1 + currentMwst);

        const currentEffectiveMargin = ekNetto > 0 ? ((vkBruttoFixStandard / ekNetto) - 1) * 100 : 0;
        let aktiveMarge, vkBruttoFix;

        const parsedTempRaw = parseFloat(String(temporaereMarge));
        const parsedTempMarge = !isNaN(parsedTempRaw)
            ? Math.max(WUNSCHMARGE_MIN, parsedTempRaw)
            : NaN;
        if (!isNaN(parsedTempMarge) && parsedTempMarge > currentEffectiveMargin) {
            aktiveMarge = parsedTempMarge;
            const bruttoRefManuell = ext.baseEkNetto * (1 + aktiveMarge / 100);
            const nettoBasisManuell = bruttoRefManuell / (1 + REFERENCE_MWST_DE);
            const wwL0 = ext.baseEkNetto * (WARENWIRTSCHAFT_FAKTOR - 1);
            const zielGewinn = nettoBasisManuell - ext.baseEkNetto - wwL0;
            const wwAktuell = ekNetto * (WARENWIRTSCHAFT_FAKTOR - 1);
            const benoetigterVkNetto = zielGewinn + ekNetto + wwAktuell;
            vkBruttoFix = benoetigterVkNetto * (1 + currentMwst);
        } else {
            aktiveMarge = currentEffectiveMargin;
            vkBruttoFix = vkBruttoFixStandard;
        }

        const vkNettoFix = vkBruttoFix / (1 + currentMwst);
        const wwKosten = ekNetto * (WARENWIRTSCHAFT_FAKTOR - 1);
        const gewinn = vkNettoFix - ekNetto - wwKosten;
        const gewinnMarge = ekNetto > 0 ? (gewinn / ekNetto) * 100 : 0;

        return { ...ext, ekNetto, verkaufsMarge: aktiveMarge, gewinnMarge, vkBruttoFix, reingewinn: gewinn };
    }) : [], [baseExtensions, selectedLevel, customMargin, temporaereMarge, currentMwst]);

    const selectedExtension = extensions.find(e => e.id === selectedExtensionId) || {};

    // MATHEMATIK (MULTI: Simplie-Logik pro Zeile, dann Aggregation)
    let matNetto = 0;
    let vkHaareBrutto = 0;
    for (const li of lineItems) {
        const ext = extensions.find(e => e.id === li.extensionId);
        if (!ext) continue;
        const stk = ext.piecesPerPack || 1;
        const ekProStueckItem = (ext.ekNetto || 0) / stk;
        const menge = Number(li.menge) || 0;
        matNetto += ekProStueckItem * menge;
        vkHaareBrutto += (ext.vkBruttoFix || 0) * menge;
    }
    const ekProStueck = gesamtMenge > 0 ? matNetto / gesamtMenge : 0;
    const stkProPaket = selectedExtension.piecesPerPack || 1;

    const wwNetto = matNetto * (WARENWIRTSCHAFT_FAKTOR - 1);
    const wwBrutto = wwNetto * (1 + currentMwst);

    const vkHaareNetto = vkHaareBrutto / (1 + currentMwst);
    const gewinnHaare = vkHaareNetto - matNetto - wwNetto;

    const arbNettoBasis = (inputs.stundensatz / (1 + REFERENCE_MWST_DE));
    const arbNetto = (inputs.einarbeitungszeit / 60) * arbNettoBasis;
    const arbBrutto = arbNetto * (1 + currentMwst);
    const gesamtBrutto = arbBrutto + vkHaareBrutto;

    const auswahlZusammenfassung = lineItems.length === 0
        ? 'Keine Haarlänge ausgewählt'
        : lineItems.map(li => {
            const ext = extensions.find(e => e.id === li.extensionId);
            const name = ext?.name || li.extensionId;
            const cmMatch = String(name).match(/(\d+)\s*cm/i);
            const label = cmMatch ? `${cmMatch[1]} cm` : name;
            return `${label} (${li.menge}x)`;
        }).join(', ');

    const gewinnProHerren = inputs.herrenhaarschnittPreis * 0.10; 
    const anzahlHerren = gewinnProHerren > 0 ? gewinnHaare / gewinnProHerren : 0;
    const anzahlStunden = anzahlHerren * 0.5;

    const countryOptions = Object.keys(COUNTRY_CONFIG).map(c => ({
        value: c,
        label: `${c} (${(COUNTRY_CONFIG[c].mwst * 100).toFixed(1)}%)`
    }));

    const levelOptions = [
        { value: 0, label: 'Ohne Rabatt' },
        { value: 1, label: 'Level 1' },
        { value: 2, label: 'Level 2' },
        { value: 3, label: 'Level 3' }
    ];

    const scrollToAdminSection = useCallback(() => {
        window.setTimeout(() => {
            adminSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
    }, []);

    const handleAdminButtonClick = () => {
        if (isAdminPanelUnlocked && isAdminVisible) {
            setIsAdminVisible(false);
            setIsAdminPanelUnlocked(false);
            return;
        }
        setAdminUnlockPassword('');
        setAdminUnlockError('');
        setIsAdminUnlockOpen(true);
    };

    const handleAdminUnlockSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (adminUnlockPassword !== '90') {
            setAdminUnlockError('Passwort ungültig. Bitte erneut versuchen.');
            return;
        }
        setIsAdminPanelUnlocked(true);
        setIsAdminVisible(true);
        setIsAdminUnlockOpen(false);
        setAdminUnlockPassword('');
        setAdminUnlockError('');
        scrollToAdminSection();
    };

    return (
        <div className="min-h-screen bg-pe-bg dark:bg-[#030303] font-sans text-pe-text pb-12 relative">
            <header className="bg-pe-elevated/95 dark:bg-[#0f0f10]/95 backdrop-blur-md border-b border-pe-border dark:border-gray-800 sticky top-0 z-40 px-3 sm:px-5 py-2 shadow-2xl overflow-x-auto">
                <div className="w-full flex flex-wrap items-center justify-start gap-x-2 gap-y-2 sm:gap-x-3">
                    <img
                        src={PE_ONE_LOGO_SRC}
                        className="h-28 sm:h-32 object-contain hidden md:block rounded shadow-sm shrink-0"
                        alt="PE One"
                    />

                    <a href="/" className="hidden lg:flex text-[10px] text-stone-500 dark:text-gray-500 hover:text-pe-accent-text uppercase tracking-widest font-bold items-center gap-1 transition-colors shrink-0">
                        <ChevronLeft size={14} /> Zurück
                    </a>

                    <img
                        src="https://www.friseur-coaching.de/wp-content/uploads/2025/09/simplie-Logofinal_croppedl.png"
                        className="h-12 sm:h-16 bg-white p-1.5 rounded-lg shadow-sm shrink-0"
                        alt="Simplie"
                    />

                    <div className="hidden xl:flex flex-col shrink-0 mr-1">
                        <h1 className="text-xl font-black tracking-wider text-pe-text uppercase drop-shadow whitespace-nowrap">EXTENSIONSKALKULATOR</h1>
                        <span className="text-[10px] text-pe-accent-text uppercase tracking-widest font-bold">Simplie Pro</span>
                    </div>

                    <div className="w-[150px] sm:w-[170px] shrink-0">
                        <NobleSelect
                            label="Land:"
                            value={selectedCountry}
                            options={countryOptions}
                            onChange={setSelectedCountry}
                        />
                    </div>

                    <LocalTooltip text="Hier kannst du deine eigene Marge aus- und gegenrechnen.">
                        <button onClick={() => setIsMarginCalcOpen(true)} className="flex items-center px-3 py-2 text-pe-accent-text transition-all bg-pe-sunken hover:bg-pe-card rounded border border-pe-border dark:border-gray-800 hover:border-[#E6C299]/50 shadow-[0_0_8px_rgba(230,194,153,0.1)] h-[42px] shrink-0" title="Aufschlagrechner">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg>
                            <span className="text-[10px] sm:text-xs font-bold uppercase ml-2 tracking-widest whitespace-nowrap">Marge prüfen</span>
                        </button>
                    </LocalTooltip>

                    <ThemeToggle />

                    {isAdmin && (
                        <button onClick={handleAdminButtonClick} className={`p-2 transition-all flex items-center gap-2 rounded border hover:border-red-500/50 h-[42px] px-3 shrink-0 ${isAdminVisible ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-300 dark:border-red-900/50' : 'bg-pe-card dark:bg-black text-stone-500 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 border-pe-border dark:border-gray-800'}`} title="System Architektur (Admin)">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest">Admin</span>
                        </button>
                    )}

                    <div className="flex items-center gap-2 shrink-0 ml-1">
                        <span className="text-[9px] text-stone-500 dark:text-gray-500 uppercase tracking-widest font-bold whitespace-nowrap">Powered by</span>
                        <img
                            src={PE_LOGO_SRC}
                            className="h-28 sm:h-32 object-contain drop-shadow-md shrink-0"
                            alt="PreisExpert"
                        />
                    </div>
                </div>
            </header>

            {successMessage && <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-[0_0_20px_rgba(34,197,94,0.3)] z-50 font-bold uppercase tracking-widest text-xs border border-green-400">{successMessage}</div>}

            <main className="mx-auto max-w-[1200px] w-full p-4 sm:p-8 mt-6 lg:mt-10 mb-20 carbon-panel carbon-weave-bg shadow-[0_30px_60px_-15px_rgba(0,0,0,1)] simplie-main-panel">
                <div className="carbon-content simplie-tooltip-section">
                    
                    <div className="mb-8 mt-4 flex flex-col items-center text-center">
                        <span className="block text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-gray-500 mb-2">
                            Aktuelle Auswahl
                        </span>
                        <span className="inline-flex max-w-full items-center justify-center rounded-xl border border-[#E6C299]/55 bg-[#E6C299]/12 px-5 py-2.5 text-2xl sm:text-3xl font-black text-pe-accent-text tracking-wide shadow-[0_0_24px_rgba(230,194,153,0.18)]">
                            {auswahlZusammenfassung}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 items-end">
                        <HeaderInput
                            label="Gesamtmenge Haare (automatisch)"
                            tooltip="Summe aller Mengen der aktivierten Haarlängen in der Artikeltabelle."
                        >
                            <div className="flex items-stretch w-full bg-pe-sunken border border-pe-border dark:border-gray-800 rounded h-[42px] overflow-hidden opacity-90 cursor-help">
                                <div className="flex-1 flex items-center justify-center relative px-1">
                                    <span className="text-pe-accent-text font-bold font-mono">{formatNumberDE(gesamtMenge)}</span>
                                    <span className="text-stone-500 dark:text-gray-500 text-xs absolute right-2 font-sans pointer-events-none">Stk</span>
                                </div>
                            </div>
                        </HeaderInput>
                        <HeaderInput label="Stundensatz">
                            <LocalTooltip text="Simplie hat 120 € als festen Mindestwert hinterlegt, der nicht unterboten werden darf." className="w-full">
                                <div className="w-full">
                                    <StepperInput 
                                        value={inputs.stundensatz} 
                                        onValueChange={(v: any) => setInputs({...inputs, stundensatz: v})} 
                                        step={5} 
                                        min={120} 
                                        unit={currentCurrency} 
                                        isGold={true}
                                    />
                                </div>
                            </LocalTooltip>
                        </HeaderInput>
                        <HeaderInput
                            label="Einarbeitungszeit"
                            tooltip="Reine Arbeitszeit fürs Einsetzen der Extensions – ohne Schnitt, Farbe oder Styling."
                        >
                            <StepperInput value={inputs.einarbeitungszeit} onValueChange={(v: any) => setInputs({...inputs, einarbeitungszeit: v})} step={5} min={5} unit="min" />
                        </HeaderInput>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <AuswertungResult 
                            label="Material-Basis" 
                            value={ekProStueck} 
                            unit={currentCurrency} 
                            subtext="pro Stück (netto)"
                            quantityLabel="1 Stk"
                            additionalInfo={
                                lineItems.length > 1
                                    ? `(Mischkalkulation · ${formatNumberDE(gesamtMenge)} Stk in Verarbeitung)`
                                    : (stkProPaket > 1 ? `(Paketpreis ${formatCurrencyStrict(selectedExtension.ekNetto, currentCurrency)} ÷ ${stkProPaket} Stk)` : '(Einzelartikel)')
                            }
                            tooltip="Netto-Materialkosten für 1 Stück nach deinem gewählten Simplie-Level (EK inkl. Level-Rabatt)."
                        />
                        <AuswertungResult 
                            label="VK-Preis Haare" 
                            value={vkHaareBrutto} 
                            unit={currentCurrency} 
                            subtext={`brutto (${(currentMwst * 100).toFixed(0)}%)`}
                            quantityLabel={`${formatNumberDE(gesamtMenge)} Stk`}
                            additionalInfo={`(inkl. ${formatCurrencyStrict(wwBrutto, currentCurrency)} WaWi · ${formatNumberDE(gesamtMenge)} Tresse${gesamtMenge === 1 ? '' : 'n'} in Verarbeitung)`}
                            tooltip={`Brutto-Verkaufspreis für ${formatNumberDE(gesamtMenge)} Stück inkl. Aufschlag und Warenwirtschaft.`}
                        />
                        <AuswertungResult 
                            label="Gesamt-Rechnungsbetrag" 
                            value={gesamtBrutto} 
                            unit={currentCurrency} 
                            subtext="Kundenpreis (Brutto)"
                            variant="highlight"
                            tooltip="Gesamtbetrag für deinen Kunden: Extensions brutto plus Einarbeitungszeit brutto."
                        />
                    </div>

                    <div className="bg-pe-sunken border border-pe-border dark:border-gray-800 rounded-xl p-8 mb-12 shadow-[inset_0_2px_12px_rgba(28,25,23,0.12)] dark:shadow-[inset_0_2px_20px_rgba(0,0,0,0.8)] simplie-tooltip-section">
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                                            <LocalTooltip text="Dein interner Netto-Gewinn an den Haaren nach Abzug von Einkauf und Warenwirtschaft." className="flex flex-col items-center text-center cursor-help w-full lg:w-auto lg:min-w-[16rem]">
                                <div className="flex flex-col items-center text-center w-full">
                                    <span className="text-stone-500 dark:text-gray-500 text-xs uppercase font-bold tracking-widest mb-3">Intern: Dein Reingewinn an den Haaren</span>
                                    <div className="flex items-baseline justify-center gap-3">
                                        <span className="text-4xl text-pe-text font-mono font-black drop-shadow-md">{formatCurrencyRounded(gewinnHaare, currentCurrency)}</span>
                                    </div>
                                    <span className="text-[10px] text-gray-600 mt-3 uppercase font-bold tracking-widest">Reiner Netto-Gewinn in Euro (nach Abzug von EK & WaWi)</span>
                                </div>
                            </LocalTooltip>

                            <div className="hidden lg:block w-px h-20 bg-gray-800"></div>

                            <div className="flex flex-col md:flex-row items-center gap-8 bg-pe-card p-5 rounded-xl border border-pe-border dark:border-gray-800 shadow-inner w-full lg:w-auto justify-center">
                                    {/* Reality-Check-Tooltip nur um Kennzahlen — nicht um das Preis-Eingabefeld */}
                                    <LocalTooltip
                                        text="Reality-Check: So viele Herrenhaarschnitte bzw. Arbeitsstunden müsstest du leisten, um denselben Gewinn wie an diesen Extensions zu erzielen."
                                        className="flex flex-col md:flex-row items-center gap-8 cursor-help"
                                        position="top"
                                    >
                                        <div className="text-stone-500 dark:text-gray-500 text-[10px] text-center md:text-right max-w-[120px] uppercase font-bold tracking-widest leading-relaxed">
                                            Um das zu verdienen, müsstest du...
                                        </div>
                                        <div className="flex items-center gap-8">
                                            <div className="flex flex-col items-center">
                                                <span className="text-3xl mb-2">💈</span>
                                                <span className="text-2xl font-black text-pe-text font-mono">{formatNumberDE(anzahlHerren, {maximumFractionDigits: 0})}</span>
                                                <span className="text-[9px] text-stone-500 dark:text-gray-500 uppercase font-bold tracking-widest mt-1">Herren bedienen</span>
                                            </div>
                                            <span className="text-gray-700 font-bold italic text-sm">oder</span>
                                            <div className="flex flex-col items-center">
                                                <span className="text-3xl mb-2">⏱️</span>
                                                <span className="text-2xl font-black text-pe-text font-mono">{formatNumberDE(anzahlStunden, {minimumFractionDigits: 1, maximumFractionDigits: 1})}</span>
                                                <span className="text-[9px] text-stone-500 dark:text-gray-500 uppercase font-bold tracking-widest mt-1">Std. arbeiten</span>
                                            </div>
                                        </div>
                                    </LocalTooltip>
                                    <div className="flex flex-col items-center md:ml-4 border-t md:border-t-0 md:border-l border-pe-border dark:border-gray-800 pt-4 md:pt-0 md:pl-6 w-full md:w-auto relative z-10">
                                        <LocalTooltip
                                            text="Brutto-Preis deines 30-Minuten-Herrenhaarschnitts – Basis für den Vergleich."
                                            className="flex flex-col items-center cursor-help mb-2"
                                            position="top"
                                        >
                                            <label className="text-[9px] text-stone-500 dark:text-gray-500 uppercase font-bold tracking-widest">
                                                Preis 30-Min. Schnitt
                                            </label>
                                        </LocalTooltip>
                                        <div className="w-28">
                                            <EditableField
                                                value={inputs.herrenhaarschnittPreis}
                                                onValueChange={(v: any) => setInputs({ ...inputs, herrenhaarschnittPreis: v })}
                                                isNumeric
                                                unit={currentCurrency}
                                                formatOptions={{ minimumFractionDigits: 2 }}
                                            />
                                        </div>
                                    </div>
                                </div>
                        </div>
                    </div>

                    <div className="border border-pe-border dark:border-gray-800 rounded-2xl bg-pe-tile-header flex flex-col mb-12 shadow-2xl simplie-article-panel">
                        <div className="bg-pe-sunken px-6 py-6 flex flex-col md:flex-row justify-between items-start md:items-center border-b border-pe-border dark:border-gray-800 gap-6 relative">
                            <h2 className="text-lg font-black text-pe-accent-text uppercase tracking-widest flex items-center gap-3 shrink-0">
                                <svg className="w-6 h-6 text-pe-accent-text" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path></svg>
                                <LocalTooltip text="Wähle deine Simplie-Tresse und das passende Konditions-Level. Die Spalten zeigen deinen EK vor und nach Level-Rabatt." className="inline-flex">
                                    <span className="cursor-help">Wähle deinen Artikel</span>
                                </LocalTooltip>
                            </h2>
                            
                            <div className="flex flex-col sm:flex-row gap-6 w-full md:w-auto bg-pe-card p-4 rounded-xl border border-pe-border dark:border-gray-800 shadow-inner">
                                <div className="flex flex-col gap-2 shrink-0">
                                    <div className="flex flex-col gap-2 w-full">
                                        <LocalTooltip text="Je nach Umsatzhöhe verändern sich deine Level. Bitte informiere dich direkt bei Simplie über deinen aktuellen Status." position="top">
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-gray-500 flex items-center gap-1.5 cursor-help w-full">
                                                Konditions-Level
                                                <svg className="w-3.5 h-3.5 text-pe-accent-text" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" /></svg>
                                            </label>
                                        </LocalTooltip>
                                        <NobleSelect 
                                            value={selectedLevel}
                                            options={levelOptions}
                                            onChange={setSelectedLevel}
                                            className="min-w-[160px]"
                                        />
                                    </div>
                                </div>
                            
                                <div className="flex flex-col gap-2 w-full sm:w-auto sm:min-w-[11.5rem]">
                                    <div className="flex justify-between items-end gap-2 min-h-[1.25rem]">
                                        <LocalTooltip
                                            text={`Überschreibt die Standard-Marge, wenn dein Wunschwert höher ist. Minimum ${WUNSCHMARGE_MIN} %.`}
                                            className="inline-flex"
                                            position="top"
                                        >
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-gray-500 cursor-help">
                                                Wunsch-Marge (optional)
                                            </label>
                                        </LocalTooltip>
                                        {/* Immer Platz reservieren — sonst springt die Zeile beim Einblenden */}
                                        <button
                                            type="button"
                                            onClick={() => setTemporaereMarge('')}
                                            disabled={temporaereMarge === ''}
                                            aria-hidden={temporaereMarge === ''}
                                            tabIndex={temporaereMarge === '' ? -1 : 0}
                                            className={`text-[9px] uppercase font-bold border px-2 py-0.5 rounded tracking-widest shrink-0 transition-colors ${
                                                temporaereMarge === ''
                                                    ? 'invisible pointer-events-none border-transparent'
                                                    : 'text-red-700 dark:text-red-400 hover:text-red-900 dark:hover:text-white bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-500/30'
                                            }`}
                                        >
                                            Löschen
                                        </button>
                                    </div>
                                    <div className="w-full sm:w-40">
                                        <StepperInput
                                            value={temporaereMarge}
                                            onValueChange={setTemporaereMarge}
                                            step={5}
                                            min={WUNSCHMARGE_MIN}
                                            unit="%"
                                            allowEmpty={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto overflow-y-visible p-4 simplie-article-table-scroll">
                            <table className="w-full text-left text-sm text-pe-muted dark:text-gray-300 border-collapse">
                                <thead className="border-b-2 border-pe-border dark:border-gray-800 text-[10px] uppercase tracking-widest text-stone-500 dark:text-gray-500">
                                    <tr>
                                        <th className="p-4 font-black w-14 text-center">Wahl</th>
                                        <th className="p-4 font-black">Artikelname</th>
                                        <th className="p-4 font-black text-center min-w-[11rem]">Menge</th>
                                        <th className={`p-4 text-center font-black ${selectedLevel !== 0 ? 'opacity-30' : ''}`}>
                                            <LocalTooltip text="Der Netto-Einkaufspreis des Artikels vor Rabatten." className="flex justify-center">
                                                <span className="cursor-help">Basis EK</span>
                                            </LocalTooltip>
                                        </th>
                                        <th className={`p-4 text-center font-black ${selectedLevel !== 1 ? 'opacity-30' : 'text-pe-accent-text'}`}>
                                            <LocalTooltip text="Deine Kondition bei Erreichen von Level 1 (10% Rabatt)." className="flex justify-center">
                                                <span className="cursor-help">Level 1</span>
                                            </LocalTooltip>
                                        </th>
                                        <th className={`p-4 text-center font-black ${selectedLevel !== 2 ? 'opacity-30' : 'text-pe-accent-text'}`}>
                                            <LocalTooltip text="Deine Kondition bei Erreichen von Level 2 (15% Rabatt)." className="flex justify-center">
                                                <span className="cursor-help">Level 2</span>
                                            </LocalTooltip>
                                        </th>
                                        <th className={`p-4 text-center font-black ${selectedLevel !== 3 ? 'opacity-30' : 'text-pe-accent-text'}`}>
                                            <LocalTooltip text="Deine maximale Kondition bei Level 3 (20% Rabatt)." className="flex justify-center">
                                                <span className="cursor-help">Level 3</span>
                                            </LocalTooltip>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-800/30">
                                    {extensions.map(ext => {
                                        const activeItem = lineItems.find(li => li.extensionId === ext.id);
                                        const isSelected = !!activeItem;
                                        return (
                                        <tr 
                                            key={ext.id} 
                                            className={`transition-all duration-200 ${isSelected ? 'bg-[#1a1400]/50 outline outline-1 outline-[#E6C299]/30 rounded-lg' : 'hover:bg-gray-900/50'}`}
                                        >
                                            <td className="p-4 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => toggleLineItem(ext.id, e.target.checked)}
                                                    className="cursor-pointer"
                                                    aria-label={`${ext.name} auswählen`}
                                                />
                                            </td>
                                            <td className="p-4 font-medium">
                                                <span className={`${isSelected ? 'text-pe-accent-text font-black text-base' : 'text-pe-muted dark:text-gray-300 font-bold'} tracking-wide`}>{ext.name}</span>
                                            </td>
                                            <td className="p-3 text-center">
                                                {isSelected ? (
                                                    <div className="w-40 mx-auto" onClick={(e) => e.stopPropagation()}>
                                                        <StepperInput
                                                            value={activeItem.menge}
                                                            onValueChange={(v: any) => updateLineItemMenge(ext.id, Number(v))}
                                                            min={1}
                                                            unit="Stk"
                                                        />
                                                    </div>
                                                ) : (
                                                    <span className="text-stone-500 dark:text-gray-600 text-xs font-bold uppercase tracking-widest">–</span>
                                                )}
                                            </td>
                                            <td className="p-3 text-center"><ReadOnlyField value={ext.baseEkNetto} isNumeric unit={currentCurrency} formatOptions={{ minimumFractionDigits: 2 }} dimmed={selectedLevel !== 0 && selectedLevel !== 4} /></td>
                                            <td className="p-3 text-center"><ReadOnlyField value={ext.baseEkNetto * (1-(levelDiscounts as any)[1])} isNumeric unit={currentCurrency} formatOptions={{ minimumFractionDigits: 2 }} dimmed={selectedLevel !== 1} /></td>
                                            <td className="p-3 text-center"><ReadOnlyField value={ext.baseEkNetto * (1-(levelDiscounts as any)[2])} isNumeric unit={currentCurrency} formatOptions={{ minimumFractionDigits: 2 }} dimmed={selectedLevel !== 2} /></td>
                                            <td className="p-3 text-center"><ReadOnlyField value={ext.baseEkNetto * (1-(levelDiscounts as any)[3])} isNumeric unit={currentCurrency} formatOptions={{ minimumFractionDigits: 2 }} dimmed={selectedLevel !== 3} /></td>
                                        </tr>
                                    )})}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="flex flex-col md:flex-row gap-8 mt-12 mb-8">
                        <div className="flex-1 p-8 rounded-2xl bg-pe-sunken border border-pe-border dark:border-gray-800 text-pe-muted dark:text-gray-400 text-sm shadow-inner">
                            <h3 className="text-pe-text font-black mb-5 uppercase tracking-widest text-xs flex items-center gap-3">
                                <svg className="w-6 h-6 text-pe-accent-text" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                Kalkulations-Hinweise
                            </h3>
                            <ul className="space-y-4 font-medium">
                                <li className="flex gap-3"><ChevronRight size={16} className="text-pe-accent-text shrink-0 mt-0.5" /> <span>Der kalkulierte Preis versteht sich <strong className="text-pe-text">ohne</strong> Schnitt oder farbliche Anpassung der eigenen Haare.</span></li>
                                <li className="flex gap-3"><ChevronRight size={16} className="text-pe-accent-text shrink-0 mt-0.5" /> <span>Empfehlung der Experten: Plane ca. <strong className="text-pe-text">60 Minuten</strong> Arbeitszeit pro Reihe ein.</span></li>
                                <li className="flex gap-3"><ChevronRight size={16} className="text-pe-accent-text shrink-0 mt-0.5" /> <span><strong className="text-pe-text">Liften:</strong> Pro Reihe empfehlen wir 30 {currentCurrency} und ca. 15 Minuten Zeitansatz.</span></li>
                            </ul>
                        </div>
                        
                        <div className="flex-1 p-8 rounded-2xl bg-gradient-to-br from-pe-elevated to-pe-bg dark:from-[#1a1400] dark:to-[#050505] border border-[#E6C299]/30 flex flex-col justify-center items-center text-center shadow-[0_0_30px_rgba(230,194,153,0.1)] relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-[#E6C299]/5 rounded-full blur-3xl"></div>
                            <h3 className="text-2xl font-black text-pe-text mb-3 tracking-widest uppercase">Mehr Klarheit für deine Preise?</h3>
                            <p className="text-sm text-pe-muted dark:text-gray-400 mb-8 font-medium max-w-sm">Lass uns auch deine anderen Dienstleistungen sicher und profitabel kalkulieren.</p>
                            <a href="https://calendly.com/friseurcoaching" target="_blank" rel="noreferrer" className="bg-[#E6C299] hover:bg-[#d4b088] text-black font-black py-4 px-8 rounded-xl uppercase tracking-widest text-xs transition-all shadow-[0_0_20px_rgba(230,194,153,0.2)] hover:shadow-[0_0_30px_rgba(230,194,153,0.4)] flex items-center gap-2">
                                Kostenlosen Termin buchen <ChevronRight size={16} />
                            </a>
                        </div>
                    </div>

                {/* 💉 CHIRURGIE-FIX: Der verlorene Admin-Block ist wieder eingefügt */}
                {isAdmin && isAdminPanelUnlocked && isAdminVisible && (
                    <div ref={adminSectionRef} className="p-8 mt-12 rounded-2xl border border-red-900/50 bg-pe-sunken shadow-[0_0_50px_rgba(220,38,38,0.1)] animate-in slide-in-from-top-4 scroll-mt-28">
                        <div className="space-y-8">
                            <div className="flex flex-col sm:flex-row justify-between items-center border-b border-pe-border dark:border-gray-800 pb-6">
                                <h2 className="text-xl font-black text-red-500 uppercase tracking-widest flex items-center gap-3">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                    System Architektur (Admin Panel)
                                </h2>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-5">
                                    <h3 className="text-xs font-bold text-pe-muted dark:text-gray-400 uppercase tracking-widest">Globale Parameter</h3>
                                    <div className="flex items-center justify-between bg-pe-card p-4 rounded-xl border border-pe-border dark:border-gray-800 shadow-inner">
                                        <label className="text-xs font-bold text-pe-muted dark:text-gray-300 uppercase tracking-widest">Standard DE-Marge (%)</label>
                                        <div className="w-24"><EditableField value={customMargin} onValueChange={handleCustomMarginChange} isNumeric unit="%" formatOptions={{minimumFractionDigits: 0}} /></div>
                                    </div>
                                    <div className="flex items-center justify-between bg-pe-card p-4 rounded-xl border border-pe-border dark:border-gray-800 shadow-inner">
                                        <label className="text-xs font-bold text-pe-muted dark:text-gray-300 uppercase tracking-widest">Push-Benachrichtigungen</label>
                                        <ToggleSwitch enabled={isNotificationsEnabled} onChange={handleToggleNotifications}/>
                                    </div>
                                </div>

                                <div className="space-y-5">
                                    <h3 className="text-xs font-bold text-pe-muted dark:text-gray-400 uppercase tracking-widest">Zugangskontrolle für Mandanten</h3>
                                    <div className="bg-pe-card p-5 rounded-xl border border-pe-border dark:border-gray-800 space-y-4 shadow-inner">
                                        <div className="flex justify-between items-center">
                                            <label className="text-[10px] font-bold text-stone-500 dark:text-gray-500 uppercase tracking-widest">User</label>
                                            <div className="w-2/3"><EditableField value={loginCredentials?.username} onValueChange={(v: any) => updateCredentialsInFirestore({...loginCredentials, username: v})} /></div>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <label className="text-[10px] font-bold text-stone-500 dark:text-gray-500 uppercase tracking-widest">Pass</label>
                                            <div className="w-2/3"><EditableField value={loginCredentials?.password} onValueChange={(v: any) => updateCredentialsInFirestore({...loginCredentials, password: v})} /></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="border border-pe-border dark:border-gray-800 rounded-xl overflow-hidden mt-8 shadow-2xl">
                                <div className="bg-pe-card px-5 py-4 border-b border-pe-border dark:border-gray-800 flex justify-between items-center">
                                    <h3 className="text-xs font-black text-pe-text uppercase tracking-widest">Artikel-Datenbank</h3>
                                </div>
                                {baseExtensions ? (
                                <div className="overflow-x-auto bg-pe-sunken p-2">
                                    <table className="w-full text-left text-sm text-pe-muted dark:text-gray-300">
                                        <tbody className="divide-y divide-gray-800/50">
                                            {baseExtensions.map(ext => (
                                                <tr key={ext.id} className="group hover:bg-pe-card transition-colors">
                                                    <td className="p-3"><EditableField value={ext.name} onValueChange={(val: any) => handleBaseExtensionChange(ext.id, 'name', val)} /></td>
                                                    <td className="p-3 w-40"><EditableField value={ext.baseEkNetto} onValueChange={(val: any) => handleBaseExtensionChange(ext.id, 'baseEkNetto', val)} isNumeric unit={currentCurrency} formatOptions={{minimumFractionDigits: 2}} /></td>
                                                    <td className="p-3 w-16 text-center">
                                                        <button onClick={() => deleteExtension(ext.id)} className="p-3 rounded-lg hover:bg-red-900/30 text-gray-600 hover:text-red-400 transition-colors border border-transparent hover:border-red-500/30" title="Löschen">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                ) : <div className="p-8 text-center text-stone-500 dark:text-gray-500 font-mono text-xs uppercase tracking-widest">Lade Datenbank...</div>}
                                <div className="p-4 border-t border-pe-border dark:border-gray-800 bg-pe-tile-header">
                                    <button onClick={addExtension} className="w-full bg-pe-card hover:bg-white hover:text-black border border-gray-700 text-pe-muted dark:text-gray-300 hover:border-white font-black py-4 rounded-xl uppercase text-xs tracking-widest transition-all">
                                        + Neuen Artikel anlegen
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                </div>
            </main>

            <Modal isOpen={isMarginCalcOpen} onClose={() => setIsMarginCalcOpen(false)} title="Aufschlagrechner">
                <SimpleMarginCalculator currency={currentCurrency} mwst={currentMwst} />
            </Modal>

            <Modal isOpen={isAdminUnlockOpen} onClose={() => setIsAdminUnlockOpen(false)} title="Admin-Bereich entsperren">
                <form onSubmit={handleAdminUnlockSubmit} className="space-y-5">
                    <p className="text-pe-muted dark:text-gray-400 text-sm leading-relaxed">
                        Zum Schutz der Systemeinstellungen bitte das Team-Passwort erneut eingeben.
                    </p>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-gray-500">Team-Passwort</label>
                        <input
                            type="password"
                            value={adminUnlockPassword}
                            onChange={(e) => {
                                setAdminUnlockPassword(e.target.value);
                                setAdminUnlockError('');
                            }}
                            autoFocus
                            className="w-full mt-2 bg-pe-input dark:bg-black border border-pe-border-strong dark:border-gray-700 rounded-lg px-4 py-3 text-pe-text font-mono focus:outline-none focus:border-[#E6C299]"
                            placeholder="••••"
                        />
                    </div>
                    {adminUnlockError && (
                        <p className="text-red-400 text-xs font-bold uppercase tracking-widest">{adminUnlockError}</p>
                    )}
                    <button
                        type="submit"
                        className="w-full bg-[#E6C299] hover:bg-[#d4b088] text-black font-black py-3 rounded-xl uppercase tracking-widest text-xs transition-all"
                    >
                        Admin-Bereich öffnen
                    </button>
                </form>
            </Modal>

            <Footer />
        </div>
    );
};

// --- MAIN PAGE EXPORT ---
export default function SimplieCalculatorPage() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [gatekeeperTeamAdmin, setGatekeeperTeamAdmin] = useState(false);
    const [firebaseP1Admin, setFirebaseP1Admin] = useState(false);
    const [userId, setUserId] = useState<string>('');
    const { adminTeam } = useAdminStore();

    const isAdmin = gatekeeperTeamAdmin || firebaseP1Admin;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            // Echte Login-User (E-Mail/Passwort etc.) — unverändert
            if (user && !user.isAnonymous) {
                setUserId(user.uid);
                setFirebaseP1Admin(resolveIsP1AdminUser(user.email, adminTeam));
                return;
            }

            // Kein Auto-signInAnonymously mehr — Gast-UID nur lokal
            setFirebaseP1Admin(false);
            setUserId(getOrCreateGuestUid());

            // Altlast: bestehende anonyme Sessions beenden (keine neuen erzeugen)
            if (user?.isAnonymous) {
                signOut(auth).catch((error) => console.warn('Anonymous signOut:', error));
            }
        });
        return () => unsubscribe();
    }, [adminTeam]);

    return (
        <>
            <CustomStyles />
            {!isAuthenticated ? (
                <div className="min-h-screen bg-pe-sunken flex flex-col">
                    <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
                        <Gatekeeper
                            onAuthenticated={() => setIsAuthenticated(true)}
                            onTeamAdminAccess={() => {
                                setGatekeeperTeamAdmin(true);
                                setIsAuthenticated(true);
                            }}
                        />
                    </div>
                    <Footer />
                </div>
            ) : (
                <CalculatorShell isAdmin={isAdmin} userId={userId} />
            )}
        </>
    );
}